import type { TraceSpanLike } from "@/components/TraceDetail/SpanDetails/types";
import { isTruncatedValueStub } from "@/queries/smart-tables/types";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const asNonEmptyString = (value: unknown): string | null => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 && trimmed !== "null" ? trimmed : null;
  }
  return typeof value === "number" && Number.isFinite(value) ? String(value) : null;
};

const asFiniteNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
};

const flattenDottedKeys = (value: unknown, prefix = ""): Record<string, unknown> => {
  if (!isRecord(value)) {
    return prefix ? { [prefix]: value } : {};
  }
  return Object.entries(value).reduce<Record<string, unknown>>((result, [key, nestedValue]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (isRecord(nestedValue)) {
      Object.assign(result, flattenDottedKeys(nestedValue, path));
    } else {
      result[path] = nestedValue;
    }
    return result;
  }, {});
};

const unwrapAnchorPayload = (rawValue: unknown): Record<string, unknown> | null => {
  if (!isRecord(rawValue) || isTruncatedValueStub(rawValue)) {
    return null;
  }
  return isRecord(rawValue.value) ? rawValue.value : rawValue;
};

const extractRequestLogId = (payload: Record<string, unknown>): number | null => {
  const directId = asFiniteNumber(payload.request_log_id);
  if (directId !== null) {
    return directId;
  }
  const requestLog = payload.request_log;
  return isRecord(requestLog) ? asFiniteNumber(requestLog.id ?? requestLog.request_id) : null;
};

export interface ParsedAnchorCellValue {
  payload: Record<string, unknown> | null;
  name: string | null;
  requestId: string | null;
  traceId: string | null;
  requestLogId: number | null;
  spanLike: TraceSpanLike | null;
}

export const parseAnchorCellValue = (rawValue: unknown): ParsedAnchorCellValue => {
  const empty: ParsedAnchorCellValue = {
    payload: null,
    name: null,
    requestId: null,
    traceId: null,
    requestLogId: null,
    spanLike: null,
  };
  if (isTruncatedValueStub(rawValue)) {
    return {
      ...empty,
      requestId: asNonEmptyString(rawValue.request_id),
      traceId: asNonEmptyString(rawValue.trace_id),
    };
  }

  const payload = unwrapAnchorPayload(rawValue);
  if (!payload) {
    return empty;
  }

  const nestedTrace = isRecord(payload.trace) ? payload.trace : null;
  const rawRecord = isRecord(rawValue) ? rawValue : null;
  const requestId = asNonEmptyString(payload.request_id) ?? asNonEmptyString(rawRecord?.request_id);
  const traceId =
    asNonEmptyString(payload.trace_id) ??
    asNonEmptyString(nestedTrace?.trace_id) ??
    asNonEmptyString(rawRecord?.trace_id) ??
    (isRecord(rawRecord?.trace) ? asNonEmptyString(rawRecord.trace.trace_id) : null);
  const requestLogId = extractRequestLogId(payload);
  const name = typeof payload.name === "string" && payload.name.length > 0 ? payload.name : null;
  const metadata = isRecord(payload.metadata) ? payload.metadata : {};
  const resource = isRecord(payload.resource) ? (payload.resource as Record<string, string>) : {};
  const spanLike: TraceSpanLike | null =
    name !== null ||
    requestLogId !== null ||
    Object.keys(metadata).length > 0 ||
    Object.keys(resource).length > 0
      ? {
          name: name ?? "",
          attributes: flattenDottedKeys(metadata),
          resource,
          request_log_id: requestLogId,
        }
      : null;

  return { payload, name, requestId, traceId, requestLogId, spanLike };
};
